import { ChangeDetectionStrategy, Component, computed, inject, output } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideExternalLink } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { environment } from '../../../../environments/environment';
import { CompanyNotificationService } from '../../../services/company-notification.service';
import { SetupProgressService } from '../../../services/setup-progress.service';
import { StorefrontService } from '../../../services/storefront.service';
import { LogoComponent } from '../../logo/logo.component';
import { MobileNavComponent } from './mobile-nav/mobile-nav.component';
import { MAIN_NAV_LINKS } from './nav-links';
import { NotificationMenuComponent } from './notification-menu/notification-menu.component';
import { UserMenuComponent } from './user-menu/user-menu.component';

@Component({
  selector: 'app-navbar',
  imports: [
    RouterLink,
    RouterLinkActive,
    NgIcon,
    LogoComponent,
    HlmButtonImports,
    MobileNavComponent,
    NotificationMenuComponent,
    UserMenuComponent,
  ],
  providers: [provideIcons({ lucideExternalLink })],
  templateUrl: './navbar.component.html',
  // Sticky belongs on the host: a sticky child only sticks inside its own parent.
  host: { class: 'sticky top-0 z-50 block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarComponent {
  private readonly setup = inject(SetupProgressService);
  private readonly storefrontService = inject(StorefrontService);
  private readonly notifService = inject(CompanyNotificationService);
  private readonly router = inject(Router);

  /** Kept for the layout's body scroll lock while the mobile sheet is open. */
  readonly onMobileMenuToggle = output<boolean>();

  protected readonly company = this.setup.company;

  protected readonly links = computed(() => {
    const company = this.company();
    if (!company) return [];
    return MAIN_NAV_LINKS.filter((link) => !link.isVisible || link.isVisible(company));
  });

  /** Network admins and their members see the Schenkwerk Networks branding. */
  protected readonly logoSubtitle = computed(() => {
    const company = this.company();
    return company?.is_group_admin || company?.is_member ? 'Networks' : null;
  });

  protected readonly companyLogoUrl = computed(() => {
    const path = this.company()?.logo_image_path;
    if (!path) return null;
    // The images endpoint already includes the uploads/ segment.
    const cleanPath = path.startsWith('uploads/') ? path.slice(8) : path;
    return `${environment.apiUrl}/images/${cleanPath}`;
  });

  constructor() {
    this.notifService.startPolling();
  }

  /** A member has no shop of its own: its customers buy at the network's shop. */
  protected readonly storefrontSuburl = computed(() => {
    const company = this.company();
    return (company?.is_member ? company.group_company_suburl : company?.suburl) ?? null;
  });

  protected openStorefront(): void {
    const suburl = this.storefrontSuburl();
    if (!suburl) return;
    const protocol = this.storefrontService.getProtocol();
    const baseDomain = this.storefrontService.getStorefrontBaseDomain();
    window.open(`${protocol}${suburl}.${baseDomain}`, '_blank');
  }

  protected navigateToRedeem(): void {
    this.router.navigate(['/redeem']);
  }
}
